import { parseDecimalInput } from '../../../../shared/numeric/localeNumeric';
import { quantityAllowsFraction, type UnitOfMeasure } from '@shared/quantity';
import { parseStockCsvExpiry, parseStockCsvMotivo, type StockCsvMotivo } from './stockCsvSchemas';

export type StockCsvRowInput = {
  sku?: string;
  cantidad?: string;
  costo_unitario?: string;
  motivo?: string;
  proveedor?: string;
  stock_minimo?: string;
  vencimiento?: string;
  lote?: string;
};

export type StockCsvParsedRow = {
  /** Cantidad a INGRESAR (se suma al stock actual). */
  quantity: number;
  /** Costo unitario del ingreso; `null` = no provisto (el llamador usa el costo actual o 0). */
  unitCost: number | null;
  reason: StockCsvMotivo;
  /** `null` = no provisto (el llamador conserva el mínimo actual). */
  minQuantity: number | null;
  expiresAt: string | null;
  lotNumber: string | null;
};

export type StockCsvRowParseResult =
  | { ok: true; value: StockCsvParsedRow }
  | { ok: false; error: string };

/**
 * Parseo puro (sin DB) de una fila del CSV de stock para un ingreso estilo "Agregar stock".
 * No resuelve el proveedor (requiere acceso a datos); eso lo hace el caller.
 */
export function parseStockCsvRowValues(
  input: StockCsvRowInput,
  ctx: { hasExistingStock: boolean; unitOfMeasure?: UnitOfMeasure | string | null }
): StockCsvRowParseResult {
  // UoM desconocida = default más seguro (entero), igual que normalizeUnitOfMeasure.
  const allowsFraction = quantityAllowsFraction(ctx.unitOfMeasure);

  const qtyRaw = input.cantidad?.trim();
  if (!qtyRaw) return { ok: false, error: 'Falta la cantidad.' };
  const quantity = parseDecimalInput(qtyRaw, {
    integerOnly: !allowsFraction,
    min: allowsFraction ? 0.0001 : 1,
  });
  if (quantity === null) {
    return {
      ok: false,
      error: allowsFraction ? 'La cantidad tiene que ser mayor a cero.' : 'La cantidad tiene que ser un entero de 1 o más.',
    };
  }

  let unitCost: number | null = null;
  const costRaw = input.costo_unitario?.trim();
  if (costRaw) {
    const c = parseDecimalInput(costRaw, { min: 0 });
    if (c === null) return { ok: false, error: 'Costo unitario inválido.' };
    unitCost = c;
  }

  const motivo = parseStockCsvMotivo(input.motivo);
  if (motivo === 'invalid') {
    return { ok: false, error: 'Motivo inválido. Usá recepcion, reposicion o alta_inicial.' };
  }
  const reason: StockCsvMotivo =
    motivo ?? (ctx.hasExistingStock ? 'stock_restock' : 'stock_initial_load');

  let minQuantity: number | null = null;
  const minRaw = input.stock_minimo?.trim();
  if (minRaw) {
    const m = parseDecimalInput(minRaw, { integerOnly: !allowsFraction, min: 0 });
    if (m === null) return { ok: false, error: 'Stock mínimo inválido.' };
    minQuantity = m;
  }

  const expiry = parseStockCsvExpiry(input.vencimiento);
  if (expiry === 'invalid') {
    return { ok: false, error: 'La fecha de vencimiento no es válida (AAAA-MM-DD).' };
  }
  const expiresAt = expiry ?? null;

  const lotNumber = input.lote?.trim() ? input.lote.trim() : null;

  return { ok: true, value: { quantity, unitCost, reason, minQuantity, expiresAt, lotNumber } };
}
